<template>
  <b-overlay :show="loading" opacity="0.5">
    <div>
      <list-table
        ref="users_table"
        :records="users" 
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        :current-page-number.sync="currentPage"
        :show-export-button="can('users:CanExport')"
        :import-type="importType"
        selection-mode="multi"
        @row-clicked="onRowClicked"
        @export="onExportUsers"
        @selected-rows="onRowsSelected"
      >
        <template v-if="can('users:CanAddNew')" #action_button>
          <b-button
            v-if="selectedUsers.length > 0"
            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
            variant="success"
            class="mr-1"
            @click="bulkAssignUserToApplications"
          >
            <feather-icon icon="GridIcon" class="mr-50" />
            <span class="align-middle">Enroll {{ selectedUsers.length }} User{{ selectedUsers.length > 1 ? 's' : '' }}</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            :to="{ name: 'new-user' }"
          >
            <span class="align-middle text-lg">New User</span>
          </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select"
                placeholder="Status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="statusFilter"
                :options="statusOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => statusFilter = val"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Member type"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="memberTypeFilter"
                :options="participant_types"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => memberTypeFilter = val"
              />
            </b-col>

            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Titles"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="titleFilter"
                :options="memberTitleOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => titleFilter = val"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Designation"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="designationFilter"
                :options="memberDesignationOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => designationFilter = val"
              />
            </b-col>
          </b-row>
          <b-row class="mt-1">
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Region"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="regionFilter"
                :options="regionOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => regionFilter = val"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Area"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="areaFilter"
                :options="areaOptions"
                :reduce="val => val.value"
                :disabled="loading || !regionFilter"
                @input="(val) => areaFilter = val"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="District"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="districtFilter"
                :options="districtOptions"
                :reduce="val => val.value"
                :disabled="loading || !areaFilter"
                @input="(val) => districtFilter = val"
              />
            </b-col>
            <b-col cols="12" md="3" class="">
              <v-select
                class="filter-select"
                placeholder="Assembly"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="assemblyFilter"
                :options="assemblyOptions"
                :reduce="val => val.value"
                :disabled="loading || !districtFilter"
                @input="(val) => assemblyFilter = val"
              />
            </b-col>
          </b-row>
          <b-row v-if="activeFilterCount > 0" class="mt-1">
            <b-col cols="12" class="d-flex align-items-center">
              <small class="text-muted mr-1">
                {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} applied
              </small>
              <b-button
                variant="flat-danger"
                size="sm"
                :disabled="loading"
                @click="clearAllFilters"
              >
                <feather-icon icon="XIcon" class="mr-50" />
                <span class="align-middle">Clear all</span>
              </b-button>
            </b-col>
          </b-row>
        </template>

        <template #cell(avatar)="data">
          <TableUserColumn :user="data.item" />
        </template>

        <template #cell(national_ID)="data">
          <span>{{ getValueFromSource(data, 'item.national_ID') || '-' }}</span>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            
            <b-dropdown-item :to="{ name: 'single-user', params: { id: data.item._id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item :to="{ name: 'single-user', params: { id: data.item._id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>

            <b-dropdown-item @click="() => assignUserToApplications(data.item)">
              <feather-icon icon="GridIcon" />
              <span class="align-middle ml-50">Edit Applications</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="can('users:CanUpdate')" @click="() => onRevokeSessions(data.item)">
              <feather-icon icon="LogOutIcon" />
              <span class="align-middle ml-50">Revoke all sessions</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="can('users:CanDelete')" @click="() => onDeleteUser(data.item)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
    <import-report-modal
      v-if="importReport"
      :show-modal.sync="showImportReportModal"
      :import-report="importReport"
      title="Users Import Report"
    />
    <b-modal
      v-model="showApplicationInvitesModal"
      hide-footer
      size="sm"
      centered
      title="Assigned Applications"
      hide-header
      no-close-on-esc
      no-close-on-backdrop
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header py-1"
        style="border-bottom: 1px solid #000000"
      >
        <h5
          class="mb-0"
          style="font-weight: 400; font-size: 14px; line-height: 160%"
        >
          Assign Applications to User
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="() => { showApplicationInvitesModal = false }"
        />
      </div>
      <p v-if="editingUser" class="mt-2 mb-1">
        Assign these applications to the user with email <b>{{ editingUser.email }}</b>
      </p>
      <v-select
        v-model="userAssignedApplications"
        class="filter-select mt-2"
        placeholder="Applications"
        :multiple="true"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="applicationsForInviteOptions"
        :reduce="val => val.value"
        :disabled="loading"
      />
      <b-button class="w-100 mt-2" @click="assignApplicationsToUser">Save User Applications</b-button>
    </b-modal>

    <b-modal
      v-model="showBulkApplicationInvitesModal"
      hide-footer
      size="md"
      centered
      title="Bulk Assign Applications"
      hide-header
      no-close-on-esc
      no-close-on-backdrop
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header py-1"
        style="border-bottom: 1px solid #000000"
      >
        <h5
          class="mb-0"
          style="font-weight: 400; font-size: 14px; line-height: 160%"
        >
          Bulk Enroll Users to Applications
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="() => { showBulkApplicationInvitesModal = false }"
        />
      </div>
      <p class="mt-2 mb-1">
        Enroll <b>{{ selectedUsers.length }}</b> selected user{{ selectedUsers.length > 1 ? 's' : '' }} to the following applications:
      </p>
      <div v-if="selectedUsers.length > 0" class="mb-2">
        <small class="text-muted">Selected Users:</small>
        <div class="d-flex flex-wrap mt-1">
          <b-badge
            v-for="user in selectedUsers"
            :key="user._id"
            variant="light-primary"
            class="mr-1 mb-1"
          >
            {{ user.name || user.email }}
          </b-badge>
        </div>
      </div>
      <v-select
        v-model="bulkAssignedApplications"
        class="filter-select mt-2"
        placeholder="Select Applications"
        :multiple="true"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="applicationsForInviteOptions"
        :reduce="val => val.value"
        :disabled="loading"
      />
      <b-button 
        class="w-100 mt-2" 
        variant="success"
        :disabled="bulkAssignedApplications.length === 0"
        @click="assignApplicationsToMultipleUsers"
      >
        Enroll Users
      </b-button>
    </b-modal>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput, BModal
} from 'bootstrap-vue'
import { get, debounce } from "lodash"

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

import FileDownload from "js-file-download";
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import TableUserColumn from "@/@core/components/shared/users/TableUserColumn.vue"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ImportReportModal from "@core/components/shared/import-report-modal/ImportReportModal.vue";

// Any filter change sends the user back to the first page, otherwise the
// narrowed result set is read from whatever page they happened to be on.
const filterWatchHandler = {
  handler(){
    this.onFilterChanged()
  },
  immediate: false
}
export default {
  components: {
    TableUserColumn,
    ListTable,
    BModal,
    ImportReportModal,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BDropdown,
    BDropdownItem,
    BPagination,
    BButton,
    BFormFile,
    BInputGroup, 
    BInputGroupAppend, 
    BFormInput,

    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    showAddUserButton: {
      type: Boolean,
      default: true
    },
  },
  data(){
    return {
      loading: false,
      importReport: null,
      showImportReportModal: false,
      showApplicationInvitesModal: false,
      showBulkApplicationInvitesModal: false,
      showFilter: false,
      showListTable: false,
      statusFilter: null,
      searchFilter: "",
      memberTypeFilter: "",
      titleFilter: "",
      designationFilter: "",
      regionFilter: "",
      areaFilter: "",
      districtFilter: "",
      assemblyFilter: "",
      users: [],
      selectedUsers: [],
      editingUser: null,
      importType: "users",
      reservationUserId: "",
      showEventReservationModal: false,
      userAssignedApplications: [],
      bulkAssignedApplications: [],
      tableColumns: [
        { key: 'check', label: '', sortable: false },
        { key: 'avatar', stickyColumn: true, sortable: false, label: 'User' },
        { key: 'member_profile.member_title.title', label: 'Title', sortable: false, },
        { key: 'member_profile.member_type.title', label: 'Member Type', sortable: false, },
        { key: 'member_profile.member_designation.designation', label: 'Designation', sortable: false, },
        { key: 'member_profile', label: "Region", sortable: false, formatter: (profile) => profile?.region_data?.title || profile?.region || '-' },
        { key: 'member_profile', label: "Area", sortable: false, formatter: (profile) => profile?.area_data?.title || profile?.area || '-' },
        { key: 'member_profile', label: "District", sortable: false, formatter: (profile) => profile?.district_data?.title || profile?.district || '-' },
        { key: 'member_profile', label: "Assembly", sortable: false, formatter: (profile) => profile?.assembly_data?.title || profile?.assembly || '-' },
        { key: 'status', sortable: false },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'In-Active', value: 'inactive' },
        { label: 'Blocked', value: 'blocked' },
        { label: 'Pending Email Verification', value: 'pending_confirmation' },
        { label: 'Pending Phone Verification', value: 'pending_sms' }
      ],
      memberTitleOptions: [],
      participant_types: [],
      memberDesignationOptions: [],
      regionOptions: [],
      areaOptions: [],
      districtOptions: [],
      assemblyOptions: [],
      applications: [],

      // Set while filters are being restored from the URL or cleared in bulk,
      // so the cascading watchers do not wipe the values being restored.
      hydratingFilters: false,
      // Guards against slow responses from earlier keystrokes overwriting the
      // results of the latest one.
      latestRequestId: 0,

      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
    dataMeta(){
      const localItemsCount = this.users.length || 0;
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalRecords,
      }
    },
    applicationsForInviteOptions() {
      return (this.applications || []).map(application => ({ label: application.title, value: application.id }));
    },
    searchTerm() {
      return String(this.searchFilter || "").trim();
    },
    activeFilterCount() {
      return [
        this.searchTerm,
        this.statusFilter,
        this.memberTypeFilter,
        this.titleFilter,
        this.designationFilter,
        this.regionFilter,
        this.areaFilter,
        this.districtFilter,
        this.assemblyFilter,
      ].filter(Boolean).length;
    },
    listQuery() {
      return this.withoutEmptyValues({
        limit: this.perPage,
        page: this.currentPage,
        status: this.statusFilter,
        search: this.searchTerm,
        member_type_id: this.memberTypeFilter,
        member_title_id: this.titleFilter,
        member_designation_id: this.designationFilter,
        region_id: this.regionFilter,
        area_id: this.areaFilter,
        district_id: this.districtFilter,
        assembly_id: this.assemblyFilter,
      });
    }
  },
  watch: {
    perPage: {
      handler(){
        this.onFilterChanged()
      },
      immediate: false
    },
    currentPage: {
      handler(){
        if (this.hydratingFilters) return;
        this.debouncedFetchUsersData()
      },
      immediate: false
    },
    statusFilter: filterWatchHandler,
    searchFilter: {
      handler(newValue, oldValue){
        // Ignore changes that only add or remove surrounding whitespace.
        if (String(newValue || "").trim() === String(oldValue || "").trim()) return;
        this.onFilterChanged();
      },
      immediate: false
    },
    memberTypeFilter: {
      handler(member_type_id) {
        if (this.hydratingFilters) return;

        this.titleFilter = "";
        this.designationFilter = "";
        this.memberTitleOptions = [];
        this.memberDesignationOptions = [];

        if (member_type_id) {
          this.fetchMemberTitlesAndDesignations(member_type_id);
        }

        this.onFilterChanged();
      },
      immediate: false
    },
    titleFilter: filterWatchHandler,
    designationFilter: filterWatchHandler,
    regionFilter: {
      handler(region_id) {
        if (this.hydratingFilters) return;

        this.areaFilter = "";
        this.districtFilter = "";
        this.assemblyFilter = "";
        this.areaOptions = [];
        this.districtOptions = [];
        this.assemblyOptions = [];

        if (region_id) {
          this.fetchAreas(region_id);
        }

        this.onFilterChanged();
      },
      immediate: false
    },
    areaFilter: {
      handler(area_id) {
        if (this.hydratingFilters) return;

        this.districtFilter = "";
        this.assemblyFilter = "";
        this.districtOptions = [];
        this.assemblyOptions = [];

        if (area_id) {
          this.fetchDistricts(area_id);
        }

        this.onFilterChanged();
      },
      immediate: false
    },
    districtFilter: {
      handler(district_id) {
        if (this.hydratingFilters) return;

        this.assemblyFilter = "";
        this.assemblyOptions = [];

        if (district_id) {
          this.fetchAssemblies(district_id);
        }

        this.onFilterChanged();
      },
      immediate: false
    },
    assemblyFilter: filterWatchHandler,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 400);

    this.fetchMemberTypes();
    this.fetchRegions();
    this.fetchPrivateAccessApplications();

    this.restoreFiltersFromRoute();
  },
  beforeDestroy(){
    if (this.debouncedFetchUsersData.cancel) {
      this.debouncedFetchUsersData.cancel();
    }
  },
  methods: {
    withoutEmptyValues(query) {
      return Object.keys(query).reduce((accumulator, key) => {
        const value = query[key];

        if (value !== null && value !== undefined && value !== "") {
          accumulator[key] = value;
        }

        return accumulator;
      }, {});
    },
    onFilterChanged() {
      if (this.hydratingFilters) return;

      // A narrower result set is always read from the first page.
      this.currentPage = 1;
      this.debouncedFetchUsersData();
    },
    async restoreFiltersFromRoute() {
      this.hydratingFilters = true;

      const {
        page = 1,
        limit = this.perPage,
        status = "",
        search = "",
        member_type_id = "",
        member_title_id = "",
        member_designation_id = "",
        region_id = "",
        area_id = "",
        district_id = "",
        assembly_id = "",
      } = this.$route.query;

      this.currentPage = Number(page) || 1;
      this.perPage = Number(limit) || this.perPage;
      this.statusFilter = status || null;
      this.searchFilter = search;
      this.memberTypeFilter = member_type_id;
      this.titleFilter = member_title_id;
      this.designationFilter = member_designation_id;
      this.regionFilter = region_id;
      this.areaFilter = area_id;
      this.districtFilter = district_id;
      this.assemblyFilter = assembly_id;

      // Load the dependent option lists so restored values render with a
      // label instead of a raw id.
      await Promise.all([
        member_type_id ? this.fetchMemberTitlesAndDesignations(member_type_id) : null,
        region_id ? this.fetchAreas(region_id) : null,
        area_id ? this.fetchDistricts(area_id) : null,
        district_id ? this.fetchAssemblies(district_id) : null,
      ]);

      // Let the watchers triggered by the assignments above run while they are
      // still suppressed, then take a single pass at the data.
      await this.$nextTick();
      this.hydratingFilters = false;

      this.fetchAllData();
    },
    async clearAllFilters() {
      this.hydratingFilters = true;

      this.searchFilter = "";
      this.statusFilter = null;
      this.memberTypeFilter = "";
      this.titleFilter = "";
      this.designationFilter = "";
      this.regionFilter = "";
      this.areaFilter = "";
      this.districtFilter = "";
      this.assemblyFilter = "";

      this.memberTitleOptions = [];
      this.memberDesignationOptions = [];
      this.areaOptions = [];
      this.districtOptions = [];
      this.assemblyOptions = [];

      await this.$nextTick();
      this.hydratingFilters = false;

      this.onFilterChanged();
    },
    async fetchAllData() {
      // Responses can come back out of order while the user is still typing,
      // only the newest request is allowed to update the table.
      this.latestRequestId += 1;
      const requestId = this.latestRequestId;

      try {
        this.loading = true;

        const query = this.listQuery;
        this.$router.replace({ query }).catch(() => {});

        const request = await this.useJwt().mainService.users.list(query);

        if (requestId !== this.latestRequestId) return;

        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = Number(pagination.limit) || this.perPage;
        this.users = data;
      } catch (error) {
        if (requestId !== this.latestRequestId) return;

        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        if (requestId === this.latestRequestId) {
          this.loading = false;
          this.showListTable = true;
        }
      }
    },
    async fetchMemberTypes() {
      try {
        this.loading = true;

        const query = {
          limit: 1000,
        }

        const request = await this.useJwt().mainService.memberTypes.list(query);
        const { data } = request.data;

        this.participant_types = data.map((participant_type) => ({
          label: participant_type.title,
          value: participant_type._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchMemberTitlesAndDesignations(member_type_id) {
      try {
        const query = {
          member_type_id
        }
        const member_title_resp = await this.useJwt().mainService.memberTitles.list(query);
        const member_designation_rep = await this.useJwt().mainService.memberDesignations.list(query);

        const member_titles = get(member_title_resp.data, "data.[0].categories");
        const member_designations = get(member_designation_rep.data, "data.[0].categories");

        this.memberTitleOptions = member_titles.map((member_title) => ({
          label: member_title.title,
          value: member_title._id,
        }));

        this.memberDesignationOptions = member_designations.map((member_designation) => ({
          label: member_designation.designation,
          value: member_designation._id,
        }));
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
          console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      }
    },
    async fetchRegions() {
      try {
        this.loading = true;

        const request = await this.useJwt().sharedService.fetchGeoRegions();
        const { data } = request.data;

        this.regionOptions = data.map((region) => ({
          label: region.title,
          value: region._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAreas(region_id) {
      try {
        this.loading = true;

        const query = { region_id };
        const request = await this.useJwt().sharedService.fetchGeoAreas(query);
        const { data } = request.data;

        this.areaOptions = data.map((area) => ({
          label: area.title,
          value: area._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchDistricts(area_id) {
      try {
        this.loading = true;

        const query = { area_id };
        const request = await this.useJwt().sharedService.fetchGeoDistricts(query);
        const { data } = request.data;

        this.districtOptions = data.map((district) => ({
          label: district.title,
          value: district._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAssemblies(district_id) {
      try {
        this.loading = true;

        const query = { district_id };
        const request = await this.useJwt().sharedService.fetchGeoAssemblies(query);
        const { data } = request.data;

        this.assemblyOptions = data.map((assembly) => ({
          label: assembly.title,
          value: assembly._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExportUsers(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        // Export exactly what the table is currently showing, minus paging.
        const { page, limit, ...query } = this.listQuery;

        const response = await this.useJwt().mainService.users.export(query);
        FileDownload(response.data, "users-export.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async fetchPrivateAccessApplications() {
      try {
        this.loading = true;

        const query = {
          limit: 1000,
          access_type: 'private',
        };

        const request = await this.useJwt().mainService.applications.list(query);
        const { data } = request.data;

        this.applications = data;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async assignApplicationsToUser() {
      try {
        if (!this.editingUser) { return }
        this.loading = true;

        await this.useJwt().mainService.applications.assignApplicationsToUser(this.editingUser._id, {
          application_ids: this.userAssignedApplications
        });

        this.editingUser = null;
        this.userAssignedApplications = [];
        this.showApplicationInvitesModal = false
        this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success!",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "User applications updated!",
          },
        });
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onImportOptionSelected(import_type) {
      this.importType = import_type
      const table_ref = this.$refs.users_table;
      if (table_ref) {
        table_ref.onTriggerSelectImportLoanRecordsCsvFile()
      }
    },
    onRowClicked(rowItem) {
      this.$router.push({ name: 'single-user', params: { id: rowItem._id } })
      .catch(() => {})
    },
    onRowsSelected(selectedRows) {
      this.selectedUsers = selectedRows;
    },
    bulkAssignUserToApplications() {
      if (this.selectedUsers.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Please select at least one user',
          },
        });
        return;
      }
      this.bulkAssignedApplications = [];
      this.showBulkApplicationInvitesModal = true;
    },
    async assignApplicationsToMultipleUsers() {
      try {
        if (this.selectedUsers.length === 0 || this.bulkAssignedApplications.length === 0) {
          return;
        }
        this.loading = true;

        const userIds = this.selectedUsers.map(user => user._id);
        const promises = userIds.map(userId => this.useJwt().mainService.applications.assignApplicationsToUser(userId, {
          application_ids: this.bulkAssignedApplications
        }));

        await Promise.all(promises);

        this.selectedUsers = [];
        this.bulkAssignedApplications = [];
        this.showBulkApplicationInvitesModal = false;
        
        // Clear selection in table
        if (this.$refs.users_table && this.$refs.users_table.$refs.listTable) {
          this.$refs.users_table.$refs.listTable.clearSelected();
        }
        
        this.fetchAllData();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success!",
            icon: "CheckCircleIcon",
            variant: "success",
            text: `Successfully enrolled ${userIds.length} user${userIds.length > 1 ? 's' : ''} to selected applications!`,
          },
        });
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    assignUserToApplications(user) {
      this.editingUser = user;
      this.userAssignedApplications = user.applications || []
      this.showApplicationInvitesModal = true
    },
    async onRevokeSessions(user) {
      try {
        const result = await this.$swal({
          title: 'Revoke all sessions?',
          text: "This signs the user out of PAC and of every application that listens for session events. They can sign in again.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, revoke.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.value) return;

        this.loading = true;
        const { data } = await this.useJwt().mainService.users.revokeSessions(user._id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Done',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `${get(data, 'data.revoked', 0)} session(s) ended`,
          },
        });
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: get(error, 'response.data.message') || error.message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDeleteUser(user) {
      try {
        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this user and related data?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.value) return;

        this.loading = true;
        await this.useJwt().mainService.users.delete(user._id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Deleted!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'User and related data deleted successfully',
          },
        });
        this.fetchAllData();
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
